/**
 * Benchmark Service — Operational Performance Comparison
 * v2 — Uses kpi_execution_metrics_v2 as primary source.
 *
 * Dimensions: role (assigned_role_id), user (completed_by), process (stage_id)
 * Metrics:    quality_score, queue_time_seconds, lead_time_seconds, sla_status
 */

import { supabase } from '../lib/supabaseClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BenchmarkRow {
  /** Dimension value: roleId | userId | stageId */
  id: string;
  /** Human-readable label */
  label: string;
  /** quality_score avg (0–100), normalised to 0–1 for display */
  efficiency: number | null;
  /** avg quality_score raw (0–100) */
  qualityScore: number | null;
  /** SLA on-time rate (0–1) */
  slaOnTimeRate: number | null;
  /** avg queue_time_seconds → minutes */
  avgQueueMinutes: number | null;
  /** avg lead_time_seconds → minutes */
  avgLeadMinutes: number | null;
  /** Number of executions in range */
  sampleSize: number;
  /** 1-based rank by efficiency desc */
  rank: number;
  /** 'top' | 'bottom' | 'middle' based on 10% thresholds */
  tier: 'top' | 'bottom' | 'middle';
}

export interface BenchmarkResult {
  rows: BenchmarkRow[];
  best: BenchmarkRow | null;
  worst: BenchmarkRow | null;
  avg: number | null;
}

export type BenchmarkDimension = 'user' | 'role' | 'process';

export interface BenchmarkFilters {
  organizationId: string;
  startDate: string; // ISO date string
  endDate: string;   // ISO date string
  minSampleSize?: number;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function classifyTiers(rows: Omit<BenchmarkRow, 'rank' | 'tier'>[]): BenchmarkRow[] {
  if (rows.length === 0) return [];

  const sorted = [...rows].sort((a, b) => {
    if (a.efficiency === null && b.efficiency === null) return 0;
    if (a.efficiency === null) return 1;
    if (b.efficiency === null) return -1;
    return b.efficiency - a.efficiency;
  });

  const total = sorted.length;
  const topThreshold = Math.max(1, Math.ceil(total * 0.1));
  const bottomThreshold = Math.max(1, Math.ceil(total * 0.1));

  return sorted.map((row, index) => {
    const rank = index + 1;
    const tier: BenchmarkRow['tier'] =
      index < topThreshold ? 'top' :
      index >= total - bottomThreshold ? 'bottom' :
      'middle';
    return { ...row, rank, tier };
  });
}

function buildResult(classified: BenchmarkRow[]): BenchmarkResult {
  const withEfficiency = classified.filter((r) => r.efficiency !== null);
  const avg = withEfficiency.length > 0
    ? withEfficiency.reduce((sum, r) => sum + r.efficiency!, 0) / withEfficiency.length
    : null;

  return {
    rows: classified,
    best: classified.find((r) => r.tier === 'top') ?? null,
    worst: classified.slice().reverse().find((r) => r.tier === 'bottom') ?? null,
    avg,
  };
}

type KpiRaw = {
  assigned_role_id: string | null;
  completed_by: string | null;
  stage_id: string | null;
  quality_score: number | null;
  sla_status: string | null;
  queue_time_seconds: number | null;
  lead_time_seconds: number | null;
};

type Bucket = {
  label: string;
  qualitySum: number; qualityN: number;
  slaOnTimeCount: number; slaTotalCount: number;
  queueSum: number; queueN: number;
  leadSum: number; leadN: number;
};

function aggregateBuckets(
  rows: KpiRaw[],
  keyFn: (r: KpiRaw) => string | null,
  labelFn: (r: KpiRaw) => string,
): Map<string, Bucket> {
  const map = new Map<string, Bucket>();
  for (const row of rows) {
    const key = keyFn(row);
    if (!key) continue;
    if (!map.has(key)) {
      map.set(key, {
        label: labelFn(row),
        qualitySum: 0, qualityN: 0,
        slaOnTimeCount: 0, slaTotalCount: 0,
        queueSum: 0, queueN: 0,
        leadSum: 0, leadN: 0,
      });
    }
    const b = map.get(key)!;
    if (row.quality_score != null) { b.qualitySum += row.quality_score; b.qualityN++; }
    if (row.sla_status != null) {
      b.slaTotalCount++;
      if (row.sla_status === 'on_time') b.slaOnTimeCount++;
    }
    if (row.queue_time_seconds != null) { b.queueSum += row.queue_time_seconds / 60; b.queueN++; }
    if (row.lead_time_seconds != null) { b.leadSum += row.lead_time_seconds / 60; b.leadN++; }
  }
  return map;
}

function bucketsToRows(
  map: Map<string, Bucket>,
  minSampleSize: number,
  labelOverrides?: Map<string, string>,
): Omit<BenchmarkRow, 'rank' | 'tier'>[] {
  const result: Omit<BenchmarkRow, 'rank' | 'tier'>[] = [];
  for (const [id, b] of map.entries()) {
    const sampleSize = b.qualityN || b.leadN || b.queueN;
    if (sampleSize < minSampleSize) continue;
    const qualityScore = b.qualityN > 0 ? b.qualitySum / b.qualityN : null;
    result.push({
      id,
      label: labelOverrides?.get(id) ?? b.label,
      efficiency: qualityScore !== null ? qualityScore / 100 : null,
      qualityScore,
      slaOnTimeRate: b.slaTotalCount > 0 ? b.slaOnTimeCount / b.slaTotalCount : null,
      avgQueueMinutes: b.queueN > 0 ? b.queueSum / b.queueN : null,
      avgLeadMinutes: b.leadN > 0 ? b.leadSum / b.leadN : null,
      sampleSize,
    });
  }
  return result;
}

// ---------------------------------------------------------------------------
// Base query — shared across dimensions
// ---------------------------------------------------------------------------

async function fetchKpiRows(
  organizationId: string,
  startDate: string,
  endDate: string,
): Promise<KpiRaw[]> {
  const { data, error } = await supabase
    .from('kpi_execution_metrics_v2')
    .select('assigned_role_id, completed_by, stage_id, quality_score, sla_status, queue_time_seconds, lead_time_seconds')
    .eq('organization_id', organizationId)
    .gte('created_at', startDate)
    .lte('created_at', endDate);

  if (error) throw error;
  console.log('benchmark kpi rows', data?.length ?? 0);
  return (data ?? []) as KpiRaw[];
}

// ---------------------------------------------------------------------------
// 1. Benchmark by Role
// ---------------------------------------------------------------------------

export async function getBenchmarkByRole(filters: BenchmarkFilters): Promise<BenchmarkResult> {
  const { organizationId, startDate, endDate, minSampleSize = 1 } = filters;
  const rows = await fetchKpiRows(organizationId, startDate, endDate);

  const buckets = aggregateBuckets(
    rows,
    r => r.assigned_role_id,
    r => r.assigned_role_id ?? '(sem cargo)',
  );

  // Resolve role names
  const roleIds = [...buckets.keys()];
  let labelMap: Map<string, string> | undefined;
  if (roleIds.length > 0) {
    const { data: roleData } = await supabase
      .from('workflow_roles_v9')
      .select('id, name')
      .in('id', roleIds);
    if (roleData && roleData.length > 0) {
      labelMap = new Map(roleData.map((r: { id: string; name: string }) => [r.id, r.name]));
    }
  }

  return buildResult(classifyTiers(bucketsToRows(buckets, minSampleSize, labelMap)));
}

// ---------------------------------------------------------------------------
// 2. Benchmark by User
// ---------------------------------------------------------------------------

export async function getBenchmarkByUser(filters: BenchmarkFilters): Promise<BenchmarkResult> {
  const { organizationId, startDate, endDate, minSampleSize = 1 } = filters;
  const rows = await fetchKpiRows(organizationId, startDate, endDate);

  const buckets = aggregateBuckets(
    rows,
    r => r.completed_by,
    r => r.completed_by ?? '(sem operador)',
  );

  // Resolve user display names
  const userIds = [...buckets.keys()];
  let labelMap: Map<string, string> | undefined;
  if (userIds.length > 0) {
    const { data: profileData } = await supabase
      .from('user_profiles')
      .select('user_id, display_name, full_name, email')
      .in('user_id', userIds);
    if (profileData && profileData.length > 0) {
      labelMap = new Map(
        profileData.map((p: { user_id: string; display_name?: string; full_name?: string; email?: string }) => [
          p.user_id,
          p.display_name ?? p.full_name ?? p.email ?? p.user_id,
        ])
      );
    }
  }

  return buildResult(classifyTiers(bucketsToRows(buckets, minSampleSize, labelMap)));
}

// ---------------------------------------------------------------------------
// 3. Benchmark by Process (stage)
// ---------------------------------------------------------------------------

export async function getBenchmarkByProcess(filters: BenchmarkFilters): Promise<BenchmarkResult> {
  const { organizationId, startDate, endDate, minSampleSize = 1 } = filters;
  const rows = await fetchKpiRows(organizationId, startDate, endDate);

  const buckets = aggregateBuckets(
    rows,
    r => r.stage_id,
    r => r.stage_id ?? '(sem etapa)',
  );

  // Resolve stage names via workflow_tasks_v9 stage_name lookup
  const stageIds = [...buckets.keys()];
  let labelMap: Map<string, string> | undefined;
  if (stageIds.length > 0) {
    const { data: stageData } = await supabase
      .from('workflow_tasks_v9')
      .select('stage_id, stage_name')
      .eq('organization_id', organizationId)
      .in('stage_id', stageIds)
      .not('stage_name', 'is', null)
      .limit(200);
    if (stageData && stageData.length > 0) {
      labelMap = new Map(
        (stageData as { stage_id: string; stage_name: string }[])
          .filter(s => s.stage_name)
          .map(s => [s.stage_id, s.stage_name])
      );
    }
  }

  return buildResult(classifyTiers(bucketsToRows(buckets, minSampleSize, labelMap)));
}
